/****************************************************
 * Module Name:  allUserByLocationPieChart.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useState } from "react";
import DashboardProxy from "../../utility/proxy/manager/dashboardProxy";
import Select from "react-select";
import Constants from "../../utility/constants";
import { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import CommonSnackbar from "../common/commonSnackBar";
import LocationProxy from "../../utility/proxy/manager/locationProxy";
import { LoadingButton } from "@mui/lab";
import { SearchOutlined } from "@mui/icons-material";
import { RiUserLocationFill } from "react-icons/ri";
import Utility from "../../utility/utility";

const deleteDisableOption = [
  { value: "*", label: "-- ANY --" },
  { value: [true], label: "True" },
  { value: [false], label: "False" },
];

const fullOptionToValue = (selectedData) => {
  if (selectedData.value === "*") return [];
  let newArr = [];
  selectedData.map((option) => {
    newArr.push(option.value);
    return true;
  });
  return newArr.includes("*") ? [] : newArr;
};

let useColor = Utility.GetRandomColorForPieChart();

const UsersByLocation = () => {
  const [oPieData, setOPieData] = useState({
    level1: [],
    level2: [],
    level3: [],
    level4: [],
  });
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [location, setLocation] = useState([]);
  const [locationOption, setLocationOption] = useState([]);
  const [deleted, setDeleted] = useState(deleteDisableOption[0]);
  const [disabled, setDisabled] = useState(deleteDisableOption[0]);
  const [userReportListLoader, setUserReportListLoader] = useState(false);
  const [fetchData, setFetchData] = useState(false);
  const defaultOptions = { value: "*", label: "-- ANY --" };

  const getAllLocationData = () => {
    LocationProxy.GetAllLocationDetailMini((response) => setDataLocation(response), setFetchData(true));
  };

  const setDataLocation = (response) => {
    let optionsArray = [];
    optionsArray.push(defaultOptions);
    response.map((data) => {
      let option = { value: data.id, label: data.name + " | " + data.country };
      optionsArray.push(option);
      return true;
    });
    setLocationOption(optionsArray);
    setLocation(
      optionsArray.length > 0 ? optionsArray[0] : location
    );
  };

  useEffect(() => {
    getAllLocationData();
  }, []);

  useEffect(() => {
    if (fetchData) {
      if (location && disabled && deleted) {
        handleSearch(location, disabled, deleted);
      }
    }
  }, [fetchData, location, deleted, disabled]);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  const calculateDashboardData = (pResponse) => {
    let response = pResponse.sort(
      (a, b) =>
        a.locationname.localeCompare(b.locationname) ||
        a.usertype.localeCompare(b.usertype) ||
        b.disabled - a.disabled ||
        b.deleted - a.deleted
    );

    let iPieData1 = {};
    response.map((res) => {
      if (!iPieData1[res.locationname]) iPieData1[res.locationname] = 0;

      iPieData1[res.locationname] += res.count;
      return true;
    });

    let iPieData2 = {};
    response.map((res) => {
      if (!iPieData2[res.locationname + "%" + res.usertype])
        iPieData2[res.locationname + "%" + res.usertype] = 0;

      iPieData2[res.locationname + "%" + res.usertype] += res.count;
      return true;
    });

    let iPieData3 = {};
    response.map((res) => {
      if (
        !iPieData3[res.locationname + "%" + res.usertype + "%" + res.disabled]
      )
        iPieData3[
          res.locationname + "%" + res.usertype + "%" + res.disabled
        ] = 0;

      iPieData3[res.locationname + "%" + res.usertype + "%" + res.disabled] +=
        res.count;
      return true;
    });

    let iPieData4 = {};
    response.map((res) => {
      if (
        !iPieData4[
        res.locationname +
        "%" +
        res.usertype +
        "%" +
        res.disabled +
        "%" +
        res.deleted
        ]
      )
        iPieData4[
          res.locationname +
          "%" +
          res.usertype +
          "%" +
          res.disabled +
          "%" +
          res.deleted
        ] = 0;

      iPieData4[
        res.locationname +
        "%" +
        res.usertype +
        "%" +
        res.disabled +
        "%" +
        res.deleted
      ] += res.count;
      return true;
    });

    let OPieData1 = [];
    Object.keys(iPieData1).map((item) => {
      OPieData1.push({ name: item, value: iPieData1[item] });
      return true;
    });

    let OPieData2 = [];
    Object.keys(iPieData2).map((item) => {
      let temp = item.split("%");
      OPieData2.push({
        name:
          temp[0] +
          " - " +
          Constants.USER_TYPE[temp[temp.length - 1]].toLowerCase(),
        value: iPieData2[item],
      });
      return true;
    });

    let OPieData3 = [];
    Object.keys(iPieData3).map((item) => {
      let temp = item.split("%");
      OPieData3.push({
        name: `${temp[0]} - ${temp[1]} - Disabled - ${temp[2]}`,
        value: iPieData3[item],
      });
      return true;
    });

    let OPieData4 = [];
    Object.keys(iPieData4).map((item) => {
      let temp = item.split("%");
      OPieData4.push({
        name: `${temp[0]} - ${temp[1]} - Deleted - ${temp[2]}`,
        value: iPieData4[item],
      });
      return true;
    });

    setOPieData({
      level1: OPieData1,
      level2: OPieData2,
      level3: OPieData3,
      level4: OPieData4,
    });
  };

  const handleSearch = (location, deleted, disabled) => {
    setUserReportListLoader(true);

    let data = {
      locationid: fullOptionToValue(location),
      deleted: deleted.value === "*" ? [false, true] : deleted.value,
      disabled: disabled.value === "*" ? [false, true] : disabled.value,
    };

    DashboardProxy.GetUsersByLocation(data, (response) => {
      setFetchData(false);

      if (response)
        calculateDashboardData(response);

      setUserReportListLoader(false);
    });
  };

  return (
    <div>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />
      <Paper elevation={4}>
        <Card>
          <CardHeader
            avatar={
              <RiUserLocationFill
                size={25}
                color={Constants.CHART_COLORS[2][0]}
              />
            }
            title={
              <Typography
                component="span"
                fontWeight={Constants.defaultFontWeight}
                fontSize={"1.25rem"}
              >
                User Bifurcation By Location
              </Typography>
            }
            subheader="No. of system users by User Type and Location"
          />
          <CardContent>
            <Box
              sx={{
                display: "flex",
                width: "700px",
                mb: 3,
                mt: 2,
                flexWrap: "wrap",
                alignItems: "center",
                gap: "10px",
                justifyContent: "space-betweeen",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  minWidth: "150px",
                  maxWidth: "auto",
                  gap: "20px",
                }}
              >
                <Box sx={{ alignSelf: "center", mt: 1, width: "65px" }}>
                  <Typography>Location</Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: "center",
                    minWidth: "150px",
                    maxWidth: "450px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    isSearchable={true}
                    name="location"
                    closeMenuOnSelect={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isMulti
                    options={locationOption}
                    value={location}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    onChange={(selectedOption) => setLocation(selectedOption)}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Typography sx={{ alignSelf: "center", width: "50px", mt: 1 }}>
                  Deleted
                </Typography>
                <Box
                  sx={{
                    minWidth: "150px",
                    maxWidth: "150px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={deleted}
                    isSearchable={true}
                    name="Deleted"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    options={deleteDisableOption}
                    onChange={(selectedOption) => setDeleted(selectedOption)}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "20px" }}>
                <Typography sx={{ alignSelf: "center", width: "50px", mt: 1 }}>
                  Disabled
                </Typography>
                <Box
                  sx={{
                    minWidth: "150px",
                    maxWidth: "150px",
                    mt: 1,
                  }}
                >
                  <Select menuPlacement="auto"
                    blurInputOnSelect={false}
                    className="basic-single"
                    classNamePrefix="select"
                    value={disabled}
                    isSearchable={true}
                    name="Disabled"
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    options={deleteDisableOption}
                    onChange={(selectedOption) => setDisabled(selectedOption)}
                  />
                </Box>
              </Box>
              <LoadingButton
                loading={userReportListLoader}
                loadingPosition="start"
                variant="contained"
                onClick={() => handleSearch(location, disabled, deleted)}
                startIcon={<SearchOutlined size="large" />}
                sx={{ mt: 1 }}
                disabled={locationOption.length === 0}
              >
                Search
              </LoadingButton>
            </Box>
            <Box height={matches ? 300 : 700} width={matches ? 320 : "45vw"}>
              <ResponsiveContainer width="99%">
                <PieChart>
                  <Pie
                    data={oPieData.level1}
                    outerRadius={80}
                    innerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#000000"
                    strokeWidth={1}
                  >
                    {oPieData.level1.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={useColor[index % useColor.length]}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={oPieData.level2}
                    outerRadius={120}
                    innerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#000000"
                    strokeWidth={1}
                  >
                    {oPieData.level2.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={useColor[index % useColor.length]}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={oPieData.level3}
                    outerRadius={160}
                    innerRadius={130}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#000000"
                    strokeWidth={1}
                  >
                    {oPieData.level3.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={useColor[index % useColor.length]}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={oPieData.level4}
                    outerRadius={200}
                    innerRadius={170}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#000000"
                    strokeWidth={1}
                  >
                    {oPieData.level4.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={useColor[index % useColor.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </ div>
  );
};

export default UsersByLocation;
